import type { ReactNode } from 'react';
import { Icon } from './Icon.js';
import { iconCheckCircle, iconError, iconInfo, iconWarning } from './generated/icons.js';
import { cx } from './util.js';

export type NoticeTone = 'info' | 'success' | 'warning' | 'error';

const ICON = {
  info: iconInfo,
  success: iconCheckCircle,
  warning: iconWarning,
  error: iconError,
} as const;

export interface NoticeProps {
  tone?: NoticeTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}

/** An inline notice. The icon and the title carry the meaning, never colour alone. */
export function Notice({ tone = 'info', title, children, action }: NoticeProps) {
  return (
    <div
      className={cx('k-notice', `k-notice--${tone}`)}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <Icon icon={ICON[tone]} size={22} />
      <div className="k-notice__text">
        {title === undefined ? null : <p className="k-t-title-sm">{title}</p>}
        {children === undefined ? null : <div className="k-t-body">{children}</div>}
      </div>
      {action === undefined ? null : <div className="k-notice__action">{action}</div>}
    </div>
  );
}

export interface BannerProps {
  tone?: 'info' | 'warning' | 'error' | 'dark';
  children: ReactNode;
  action?: ReactNode;
}

/** A page wide banner: trial, grace, read-only, deletion, offline, impersonation. */
export function Banner({ tone = 'info', children, action }: BannerProps) {
  return (
    <div className={cx('k-banner', `k-banner--${tone}`)} role="status">
      <div className="k-banner__text k-t-label">{children}</div>
      {action === undefined ? null : <div className="k-banner__action">{action}</div>}
    </div>
  );
}
